// Analytics.ts

// Tipos e chamadas do PHP que consulta o Google Analytics
// (api/includes/services/google_analytics/data_analytics.php). A chamada passa
// pelo api/index.php como type_page 'analytics', que valida sessao e token
// antes de incluir o arquivo.

import Axios from "axios";

import Env from "../../services/Env";

export interface AnalyticsCity {
    city               : string;
    sessions           : number;
    users              : number;
    avg_time_sec       : number;
    avg_time_formatted : string;
}

export interface AnalyticsRegion {
    region             : string;
    sessions           : number;
    users              : number;
    avg_time_sec       : number;
    avg_time_formatted : string;
    cities             : AnalyticsCity[];
}

export interface AnalyticsCountry {
    country            : string;
    sessions           : number;
    users              : number;
    avg_time_sec       : number;
    avg_time_formatted : string;
    regions            : AnalyticsRegion[];
}

export interface AnalyticsMinute {
    min   : string;
    users : number;
}

export interface AnalyticsCountryUsers {
    country : string;
    users   : number;
}

export interface AnalyticsRealtime {
    active_users  : number;
    per_minute    : AnalyticsMinute[];
    top_countries : AnalyticsCountryUsers[];
}

export interface AnalyticsGender {
    gender  : string;
    users   : number;
    percent : number;
}

export interface AnalyticsLanguage {
    language : string;
    users    : number;
}

export interface AnalyticsAgeBracket {
    age_bracket : string;
    users       : number;
}

// usado no gráfico depois do age_bracket virar age
export interface AnalyticsAge {
    age   : string;
    users : number;
}

export interface AnalyticsGeneralResponse {
    location?     : AnalyticsCountry[];
    realtime?     : AnalyticsRealtime;
    gender?       : AnalyticsGender[];
    languages?    : AnalyticsLanguage[];
    age_brackets? : AnalyticsAgeBracket[];
}

export interface AnalyticsApartment {
    client           : string;
    project          : string;
    apartment_number : string;
    floorplan_title  : string;
    status           : string;
    country          : string;
    region           : string;
    city             : string;
    event_count      : number;
    users            : number;
}

export interface AnalyticsUnitsResponse {
    apartments? : AnalyticsApartment[];
}

export interface AnalyticsFloorplan {
    client              : string;
    project             : string;
    floorplan_title     : string;
    floorplan_bathrooms : string;
    floorplan_bedrooms  : string;
    floorplan_size      : string;
    country             : string;
    region              : string;
    city                : string;
    event_count         : number;
    users               : number;
}

export interface AnalyticsFloorplansResponse {
    floorplans? : AnalyticsFloorplan[];
}

type TypeData = 'data_general' | 'data_units' | 'data_floorplans';

// o PHP le o $_POST['type_data'], entao a chamada vai como FormData.
// "projects" e opcional: quando vem preenchido o PHP filtra as consultas por
// customEvent:project, que e como o Analytics identifica o cliente.
// O 'type_page' roteia no index.php, que confere o token contra a sessao antes
// de incluir o data_analytics.php.
async function ShowDataAnalytics<T>(typeData: TypeData, projects?: string[]): Promise<T> {
    const data = new FormData();
    data.append('type_page', 'analytics');
    data.append('type_data', typeData);
    data.append('token', localStorage.getItem('token') || '');

    (projects ?? []).forEach((name)=>{
        data.append('projects[]', name);
    });

    const response = await Axios({
        url    : Env.apiUrl,
        method : 'POST',
        data   : data
    });

    return response.data as T;
}

export function GetDataGeneral(projects?: string[]): Promise<AnalyticsGeneralResponse> {
    return ShowDataAnalytics<AnalyticsGeneralResponse>('data_general', projects);
}

export function GetDataUnits(): Promise<AnalyticsUnitsResponse> {
    return ShowDataAnalytics<AnalyticsUnitsResponse>('data_units');
}

export function GetDataFloorplans(): Promise<AnalyticsFloorplansResponse> {
    return ShowDataAnalytics<AnalyticsFloorplansResponse>('data_floorplans');
}

// "(not set)" e variações chegam do Analytics quando o evento não mandou o dado
export function IsNotSet(value: string | null | undefined): boolean {
    if(!value){
        return true;
    }
    return ['(not set)', 'not set', '(não definido)', 'não definido'].includes(String(value).trim().toLowerCase());
}
